/* eslint-disable react/prop-types */
import * as React from 'react';

import { useLockFn, useMemoizedFn, useRequest } from 'ahooks';
import {
  Button,
  Col,
  Empty,
  Flex,
  Form,
  notification,
  Popconfirm,
  Row,
  Select,
  Typography,
} from 'antd';

import {
  AntdUpload,
  CheckOutlinedIcon,
  DeleteOutlinedIcon,
  FileImageOutlinedIcon,
  PlusOutlinedIcon,
  PrimitiveCollapse,
  PrimitiveForm,
  PrimitiveTreeSelect,
  QuestionCircleFilledIcon,
} from 'components';
import { getBrands, getCategories, updatePageBanners } from 'services';
import type {
  FileOwnerType,
  PageBannersSettings,
  PageBuilderBanner,
  PageSettingCode,
  SelectOption,
  TreeSelectSource,
} from 'types';
import {
  generateDownloadFile,
  generateSelectOptions,
  generateTreeSelectData,
  localizePageSettingCode,
  normalizeToString,
  randomId,
} from 'utils';

type BannerRow = PageBuilderBanner & {
  key: string;
};

const createEmptyBanner = (): BannerRow => ({
  key: randomId(),
  bannerId: '',
  mobileBannerId: '',
  categorySlug: '',
  brandSlug: '',
});

interface PageBuilderBannerItemProps {
  index: number;
  banner: BannerRow;
  ownerType: FileOwnerType;
  brandsOptions: SelectOption[];
  brandsLoading: boolean;
  categoriesTreeData: TreeSelectSource[];
  categoriesLoading: boolean;
  onPatch: (patch: Partial<PageBuilderBanner>) => void;
  onRemove: () => void;
}

const PageBuilderBannerItem = React.memo<PageBuilderBannerItemProps>(
  ({
    index,
    banner,
    ownerType,
    brandsOptions,
    brandsLoading,
    categoriesTreeData,
    categoriesLoading,
    onPatch,
    onRemove,
  }) => {
    const { bannerId, mobileBannerId, categorySlug, brandSlug } = banner;

    return (
      <div className='mb-4 rounded border border-solid border-[--ant-color-border-secondary] p-4'>
        <Flex justify='space-between' align='center' className='mb-3'>
          <Typography.Text strong>{`بنر ${index + 1}`}</Typography.Text>
          <Popconfirm
            title='حذف بنر'
            description='با حذف این بنر، اطلاعات آن پاک می‌شود.'
            okButtonProps={{ className: 'bg-[--ant-color-error]' }}
            icon={
              <QuestionCircleFilledIcon className='text-[--ant-color-error]' />
            }
            onConfirm={onRemove}
          >
            <Button
              danger
              type='text'
              size='small'
              icon={<DeleteOutlinedIcon />}
            >
              حذف
            </Button>
          </Popconfirm>
        </Flex>
        <Row>
          <Col span={24} lg={12} className='lg:pe-3'>
            <Form.Item label='تصویر دسکتاپ' valuePropName='file'>
              <AntdUpload
                className='[&_.ant-upload-list]:h-40'
                listType='picture-card'
                ownerType={ownerType}
                fileList={
                  bannerId
                    ? [
                        {
                          uid: bannerId,
                          name: 'تصویر دسکتاپ',
                          status: 'done',
                          url: generateDownloadFile(ownerType, bannerId),
                        },
                      ]
                    : undefined
                }
                onChange={({ file: { status, response } }) => {
                  if (status === 'done' && response) {
                    onPatch({ bannerId: response.id });
                  }
                  if (status === 'error' || status === 'removed') {
                    onPatch({ bannerId: '' });
                  }
                }}
              >
                {!bannerId ? (
                  <Flex
                    vertical
                    justify='center'
                    align='center'
                    gap='small'
                    className='mx-1'
                  >
                    <FileImageOutlinedIcon className='text-xl text-[--ant-color-link-hover]' />
                    <Typography.Text type='secondary'>
                      بارگذاری تصویر
                    </Typography.Text>
                  </Flex>
                ) : null}
              </AntdUpload>
            </Form.Item>
          </Col>
          <Col span={24} lg={12} className='lg:ps-3'>
            <Form.Item label='تصویر موبایل' valuePropName='file'>
              <AntdUpload
                className='[&_.ant-upload-list]:h-40'
                listType='picture-card'
                ownerType={ownerType}
                fileList={
                  mobileBannerId
                    ? [
                        {
                          uid: mobileBannerId,
                          name: 'تصویر موبایل',
                          status: 'done',
                          url: generateDownloadFile(ownerType, mobileBannerId),
                        },
                      ]
                    : undefined
                }
                onChange={({ file: { status, response } }) => {
                  if (status === 'done' && response) {
                    onPatch({ mobileBannerId: response.id });
                  }
                  if (status === 'error' || status === 'removed') {
                    onPatch({ mobileBannerId: '' });
                  }
                }}
              >
                {!mobileBannerId ? (
                  <Flex
                    vertical
                    justify='center'
                    align='center'
                    gap='small'
                    className='mx-1'
                  >
                    <FileImageOutlinedIcon className='text-xl text-[--ant-color-link-hover]' />
                    <Typography.Text type='secondary'>
                      بارگذاری تصویر
                    </Typography.Text>
                  </Flex>
                ) : null}
              </AntdUpload>
            </Form.Item>
          </Col>
          <Col span={24} lg={12} className='lg:pe-3'>
            <Form.Item label='دسته‌بندی مرتبط'>
              <PrimitiveTreeSelect<string>
                placeholder='دسته‌بندی مرتبط را انتخاب کنید'
                value={categorySlug || undefined}
                loading={categoriesLoading}
                treeData={categoriesTreeData}
                onChange={value => {
                  onPatch({ categorySlug: normalizeToString(value ?? '') });
                }}
              />
            </Form.Item>
          </Col>
          <Col span={24} lg={12} className='lg:ps-3'>
            <Form.Item label='برند مرتبط'>
              <Select<string>
                allowClear
                showSearch
                placeholder='برند مرتبط را انتخاب کنید'
                value={brandSlug || undefined}
                loading={brandsLoading}
                options={brandsOptions}
                filterOption={(inputValue, option) =>
                  normalizeToString(option?.label)
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                }
                onChange={value => {
                  onPatch({ brandSlug: normalizeToString(value ?? '') });
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>
    );
  },
);
PageBuilderBannerItem.displayName = 'PageBuilderBannerItem';

interface PageBuilderBannersPanelProps {
  code: PageSettingCode;
  banners: PageBuilderBanner[];
  ownerType: FileOwnerType;
  brandsOptions: SelectOption[];
  brandsLoading: boolean;
  categoriesTreeData: TreeSelectSource[];
  categoriesLoading: boolean;
}

const PageBuilderBannersPanel = ({
  code,
  banners: initialBanners,
  ownerType,
  brandsOptions,
  brandsLoading,
  categoriesTreeData,
  categoriesLoading,
}: PageBuilderBannersPanelProps) => {
  const [form] = Form.useForm();
  const [banners, setBanners] = React.useState<BannerRow[]>(() =>
    initialBanners.map(banner => ({ ...banner, key: randomId() })),
  );

  const handlePatchBanner = useMemoizedFn(
    (key: string, patch: Partial<PageBuilderBanner>) => {
      setBanners(previousBanners =>
        previousBanners.map(banner =>
          banner.key === key ? { ...banner, ...patch } : banner,
        ),
      );
    },
  );
  const handleAddBanner = useMemoizedFn(() => {
    setBanners(previousBanners => [...previousBanners, createEmptyBanner()]);
  });
  const handleRemoveBanner = useMemoizedFn((key: string) => {
    setBanners(previousBanners =>
      previousBanners.filter(banner => banner.key !== key),
    );
  });

  const onFinish = useLockFn(async () => {
    const payload = banners.map(
      ({ bannerId, mobileBannerId, categorySlug, brandSlug }) => ({
        bannerId,
        mobileBannerId,
        categorySlug,
        brandSlug,
      }),
    );

    return updatePageBanners({ code, banners: payload })
      .then(() => {
        notification.success({
          message: `«${localizePageSettingCode(code).label}» با موفقیت ذخیره شد.`,
        });
      })
      .catch(message => {
        notification.error({ message });
      });
  });

  return (
    <PrimitiveForm form={form} className='my-0' onFinish={onFinish}>
      {banners.length ? (
        banners.map((banner, index) => (
          <PageBuilderBannerItem
            key={banner.key}
            index={index}
            banner={banner}
            ownerType={ownerType}
            brandsOptions={brandsOptions}
            brandsLoading={brandsLoading}
            categoriesTreeData={categoriesTreeData}
            categoriesLoading={categoriesLoading}
            onPatch={patch => {
              handlePatchBanner(banner.key, patch);
            }}
            onRemove={() => {
              handleRemoveBanner(banner.key);
            }}
          />
        ))
      ) : (
        <Empty
          className='py-6'
          description='هنوز بنری برای این جایگاه افزوده نشده است.'
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
      <Flex justify='space-between' align='center' gap='small' className='mt-2'>
        <Button
          type='dashed'
          icon={<PlusOutlinedIcon />}
          onClick={handleAddBanner}
        >
          افزودن بنر
        </Button>
        <Form.Item className='mb-0'>
          <Button type='primary' htmlType='submit' icon={<CheckOutlinedIcon />}>
            ذخیره تغییرات
          </Button>
        </Form.Item>
      </Flex>
    </PrimitiveForm>
  );
};

type PageBuilderBannersProps = {
  settings: PageBannersSettings;
  ownerType: FileOwnerType;
};

const PageBuilderBanners = ({
  settings,
  ownerType,
}: PageBuilderBannersProps) => {
  const { data: brandsData, loading: brandsLoading } = useRequest(getBrands, {
    cacheKey: 'brands',
  });
  const { data: categoriesData, loading: categoriesLoading } = useRequest(
    getCategories,
    {
      cacheKey: 'categories',
    },
  );

  const { brands = [] } = brandsData ?? {};
  const { categories = [] } = categoriesData ?? {};
  const brandsSelectOptions = generateSelectOptions(
    brands,
    'id',
    'title',
    'slug',
  );
  const categoriesTreeData = generateTreeSelectData(
    categories,
    'id',
    'title',
    'slug',
  );

  const positionOf = (code: string) =>
    Number(code.match(/_Pos(\d+)_/i)?.at(1) ?? 0);
  const entries = Object.entries(settings).sort(
    ([firstCode], [secondCode]) =>
      positionOf(firstCode) - positionOf(secondCode) ||
      firstCode.localeCompare(secondCode),
  );

  if (!entries.length) {
    return (
      <Empty className='py-10' description='هیچ جایگاه بنری وجود ندارد.' />
    );
  }

  return (
    <PrimitiveCollapse
      defaultActiveKey={entries.at(0)?.at(0) as string}
      items={entries.map(([code, banners]) => ({
        key: code,
        label: localizePageSettingCode(code).label,
        children: (
          <PageBuilderBannersPanel
            code={code}
            banners={banners}
            ownerType={ownerType}
            brandsOptions={brandsSelectOptions}
            brandsLoading={brandsLoading}
            categoriesTreeData={categoriesTreeData}
            categoriesLoading={categoriesLoading}
          />
        ),
      }))}
    />
  );
};

export { PageBuilderBanners };
